export interface IDirtyResult {
	isDirty: KnockoutComputed<boolean>;
	reset: () => void;
	getDiff: () => { initial: ICaptureItem[], current: ICaptureItem[] };
	captureState: (dest: ICaptureItem[]) => void;
}

export interface ICaptureItem {
	key: string;
	value: any;
}

function isDirtyFlag(item: any): item is IDirtyResult {
	return item
		&& item.isDirty
		&& item.reset
		&& item.captureState;
}

function captureItemsAreEqual(a: ICaptureItem, b: ICaptureItem) {
	return a.key === b.key && a.value === b.value;
}

export class DirtyFlag {
	public static readonly create = (root: any, excludedKeys: string[] = []): IDirtyResult => {
		let captureState = (dest: ICaptureItem[]) => {
			let trackSingleItem = (key: string, value: any) => {
				if (typeof value !== "object") {
					dest.push({ key, value });
					return;
				}

				let dirtyFlag = value && value.dirtyFlag;
				if (isDirtyFlag(dirtyFlag)) {
					dest.push({ key: "$push-dto", value: key });
					dirtyFlag.captureState(dest);
					dest.push({ key: "$pop-dto", value: null });
				} else if (Array.isArray(value)) {
					dest.push({ key: "$push-arr", value: key });
					for (let item of value) {
						trackSingleItem(key, item);
					}
					dest.push({ key: "$pop-arr", value: null });
				} else {
					dest.push({ key, value: JSON.stringify(value) });
				}
			};

			for (const key in root) {
				if (root.hasOwnProperty(key)) {
					const element = root[key];

					if (!ko.isObservable(element)) {
						continue;
					}

					if (ko.isComputed(element)) {
						continue;
					}

					if (excludedKeys.includes(key)) {
						continue;
					}

					trackSingleItem(key, element());
				}
			}
		};

		let getCurrentState = () => {
			let current: ICaptureItem[] = [];
			captureState(current);
			return current;
		};

		let initialState = ko.observable(getCurrentState());

		let isDirty = ko.pureComputed(() => {
			let initial = initialState();
			let current = getCurrentState();

			if (initial.length !== current.length) {
				return true;
			}

			for (let i = 0; i < initial.length; i++) {
				if (!captureItemsAreEqual(initial[i], current[i])) {
					return true;
				}
			}

			return false;
		}).extend({ deferred: true });

		let getDiff = () => {
			let initial = initialState();
			let current = getCurrentState();
			return { initial, current };
		};

		let reset = () => {
			initialState(getCurrentState());
		};
		return { isDirty, reset, getDiff, captureState };
	}
}